// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { type FulfillmentOrderStatus } from '@/fulfillment/constants/fulfillment-order-status';
import { sparseFulfillmentSchema } from '@/sparse/api/types';
import { zodCatch } from '@/utilities/zod-catch';
import { z } from 'zod';

const PATH = '/v1/sparse/fulfillment';

const responseSchema = z
  .object({
    data: z.array(sparseFulfillmentSchema).catch(zodCatch([])),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    fulfillments: data,
  }));

export interface GetSparseFulfillmentsOptions {
  statuses?: FulfillmentOrderStatus[];
  ignore3pl?: boolean;
}

/**
 * Requests sparse fulfillment orders from the remote API.
 * This endpoint returns a lightweight list of fulfillment orders for autocomplete/dropdown use cases.
 */
export async function getSparseFulfillments(
  options: GetSparseFulfillmentsOptions = {}
) {
  const params = new URLSearchParams();

  // Add statuses if provided
  if (options.statuses && options.statuses.length > 0) {
    options.statuses.forEach((status) => {
      params.append('statuses', status);
    });
  }

  // Add ignore3pl parameter
  if (options.ignore3pl) {
    params.append('ignore3pl', 'true');
  }

  const response = await api.get(PATH, { params });
  const result = await parseResponse(responseSchema, response.data, {
    schemaName: 'SparseFulfillments',
    endpoint: PATH,
  });
  return result;
}
